import axios from "axios";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import Modal from "../../../components/Modal/Modal";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";

const ActivityModal = ({ showModal, onClose, queryParams, advanceFilters }) => {
  const [config] = useAuth();
  const [loading, setLoading] = useState(false);
  const [activityData, setActivityData] = useState([]);

  // Fetch activity data from the contact-list API
  const fetchActivityData = async () => {
    setLoading(true);

    const validAdvanceFilters = advanceFilters.filter(group =>
      group.filters && group.filters.some(f => f.field && f.operator)
    );

    const advanceFiltersQuery = encodeURIComponent(JSON.stringify(validAdvanceFilters));

    try {
      const response = await axios.get(
        `${BASE_URL}/contact-list?${queryParams}&advance_filters=${advanceFiltersQuery}`,
        config
      );

      const activities = response?.data?.data?.activity || [];
      setActivityData(activities);
    } catch (err) {
      if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
        NotificationManager.error(err.response?.data?.message || "Failed to fetch activity data");
      }
      console.error("Error fetching activity data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showModal) {
      fetchActivityData();
    }
  }, [showModal, queryParams, advanceFilters]);

  // Calculate total activities
  const totalActivities = activityData.reduce((sum, item) => sum + item.count, 0);

  // Format number with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Get icon/emoji for each interaction type
  const getActivityIcon = (type) => {
    const icons = {
      "Conversation": "💬",
      "Email": "📧",
      "Left Message": "📞",
      "Mail": "✉️",
      "Meeting": "🤝",
      "Note": "📝",
      "Text": "💬",
    };
    return icons[type] || "📌";
  };

  // Calculate percentage
  const getPercentage = (count) => {
    if (totalActivities === 0) return 0;
    return ((count / totalActivities) * 100).toFixed(1);
  };

  return (
    <Modal
      show={showModal}
      title="Activity Overview"
      onClose={onClose}
      disableContentWrapperStyle={true}
    >
      <div className="pr-5 overflow-y-auto max-h-[70vh] mt-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-10 h-10 border-3 border-gray-200 border-t-green-500 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600">Loading activities...</p>
          </div>
        ) : activityData.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-lg text-gray-600">No activity data found</p>
          </div>
        ) : (
          <>
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-2 text-center mb-6 shadow-lg">
              <p className="text-white/90 font-medium mb-2">Total Activities</p>
              <h3 className=" font-bold text-white">{formatNumber(totalActivities)}</h3>
            </div>
            {/* Activity List */}
            <div className="flex flex-col gap-4">
              {activityData.map((activity, index) => (
                <div
                  key={index}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:border-green-500 hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 relative overflow-hidden"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="head-4 w-10 h-10 flex items-center justify-center bg-white rounded-lg shadow-sm">
                      {getActivityIcon(activity.interaction_type)}
                    </span>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{activity.interaction_type}</p>
                      <p className="text-xs text-gray-600">{getPercentage(activity.count)}% of total</p>
                    </div>
                    <div className="text-right">
                      <p className="head-2 text-green-600">{formatNumber(activity.count)}</p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mt-2">
                    <div
                      className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all duration-700 ease-out"
                      style={{ width: `${getPercentage(activity.count)}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ActivityModal;